import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ToastrService } from 'ngx-toastr';

import {
  ConnectionStatus,
  OemMachine,
  Provider,
} from '../../../api-clients/oem-connection';
import { IHarvester } from '../../../models/Harvester';
import { DialogService } from '../../../services/dialog.service';
import { HarvestService } from '../../../services/harvest.service';
import { OemIntegrationService } from '../../../services/oem-integration.service';

interface RowState {
  selectedHarvesterId?: number;
  busy: boolean;
}

@Component({
    selector: 'app-machine-link-panel',
    templateUrl: './machine-link-panel.component.html',
    styleUrls: ['./machine-link-panel.component.css'],
    providers: [HarvestService],
    standalone: false
})
export class MachineLinkPanelComponent implements OnChanges {
  @Input() provider!: Provider;
  @Input() providerLabel = '';
  /**
   * True when this panel needs at least one Deere org in `granted` state
   * before it can show machines (D9). For CNH this is always false.
   */
  @Input() grantsGated = false;
  @Input() status?: ConnectionStatus;
  /**
   * Optional harvester to preselect in each unlinked machine's picker
   * (SRA003-457 — the harvester-page entry point passes the harvester being
   * edited). Left unset by the connection page, whose behaviour is unchanged.
   */
  @Input() preselectHarvesterId?: number;

  loading = false;
  errorMessage = '';
  /** True when the BE returned a 409 because no granted Deere orgs exist. */
  needsGrantHint = false;

  machines: OemMachine[] = [];
  harvesters: IHarvester[] = [];
  rowState: Record<string, RowState> = {};

  constructor(
    private oemSvc: OemIntegrationService,
    private harvestSvc: HarvestService,
    private dialog: DialogService,
    private toastr: ToastrService,
  ) {}

  ngOnChanges(_changes: SimpleChanges): void {
    if (!this.provider) return;
    this.loadHarvesters();
    if (this.disabled) {
      this.machines = [];
      return;
    }
    this.loadMachines();
  }

  /**
   * Disabled when this is a Deere panel and no orgs are in `granted` state
   * yet. The parent passes the active connection status so we can read the
   * granted count without re-fetching.
   */
  get disabled(): boolean {
    if (!this.grantsGated) return false;
    const granted = this.status?.orgs?.granted ?? 0;
    return granted < 1;
  }

  loadHarvesters(): void {
    this.harvestSvc.listHarvesters().subscribe({
      next: (harvesters) => {
        this.harvesters = (harvesters ?? []).filter((h) => h?.id != null);
      },
      error: () => {
        // Surface but don't block the panel - linking just won't work
        // until harvesters can be fetched.
        this.harvesters = [];
      },
    });
  }

  loadMachines(): void {
    this.loading = true;
    this.errorMessage = '';
    this.needsGrantHint = false;
    this.oemSvc.listMachines(this.provider).subscribe({
      next: (machines) => {
        this.machines = machines ?? [];
        this.rowState = {};
        for (const m of this.machines) {
          this.rowState[m.oem_machine_id] = {
            busy: false,
            selectedHarvesterId: this.preselectHarvesterId,
          };
        }
        this.loading = false;
      },
      error: (err: HttpErrorResponse) => {
        this.loading = false;
        if (err?.status === 409 && this.provider === Provider.Deere) {
          this.needsGrantHint = true;
          this.machines = [];
        } else {
          this.errorMessage = `Could not load ${this.providerLabel} machines`;
        }
      },
    });
  }

  rowFor(machine: OemMachine): RowState {
    let state = this.rowState[machine.oem_machine_id];
    if (!state) {
      state = { busy: false };
      this.rowState[machine.oem_machine_id] = state;
    }
    return state;
  }

  link(machine: OemMachine): void {
    const state = this.rowFor(machine);
    if (state.selectedHarvesterId == null) {
      this.toastr.warning('Select a Harvester to link to first');
      return;
    }
    state.busy = true;
    this.oemSvc
      .linkMachine(this.provider, machine.oem_machine_id, state.selectedHarvesterId)
      .subscribe({
        next: () => {
          state.busy = false;
          this.toastr.success(`Linked ${machine.oem_machine_name}`);
          this.loadMachines();
        },
        error: (err: HttpErrorResponse) => {
          state.busy = false;
          // SRA003-516: reject-don't-replace. The BE returns 409
          // errorType="already_linked" with a fully-composed friendly
          // errorMessage naming the incumbent ("This harvester is already
          // linked to CaseIH (xxx). Unlink it first." — CNH renders as
          // "CaseIH"). The incumbent is baked into the string, so show it
          // verbatim; nothing to extract. Note the envelope key is errorType
          // (camelCase), not the snake_case code/reason used elsewhere. Fall
          // back to the generic toast only when no message is present.
          const body = err?.error;
          if (
            err?.status === 409 &&
            body?.errorType === 'already_linked' &&
            body?.errorMessage
          ) {
            this.toastr.error(body.errorMessage);
          } else {
            this.toastr.error(`Could not link ${machine.oem_machine_name}`);
          }
        },
      });
  }

  unlink(machine: OemMachine): void {
    this.dialog
      .confirmDialog({
        title: 'Remove machine linkage',
        message: `Unlink ${machine.oem_machine_name} from ${this.harvesterLabel(machine.linked_harvester_id)}? Telemetry will stop being attributed to that harvester until you re-link.`,
        confirmCaption: 'Remove linkage',
        cancelCaption: 'Cancel',
      })
      .subscribe((yes) => {
        if (!yes) return;
        const state = this.rowFor(machine);
        state.busy = true;
        this.oemSvc
          .unlinkMachine(this.provider, machine.oem_machine_id)
          .subscribe({
            next: () => {
              state.busy = false;
              this.toastr.success(`Unlinked ${machine.oem_machine_name}`);
              this.loadMachines();
              this.oemSvc.requestRefresh();
            },
            error: () => {
              state.busy = false;
              this.toastr.error(`Could not unlink ${machine.oem_machine_name}`);
            },
          });
      });
  }

  isLinked(m: OemMachine): boolean {
    return m.linked_harvester_id != null;
  }

  /**
   * SRA003-524: this OEM machine is already linked by ANOTHER contractor in the
   * org (the BE org-wide overlay flag). The picker disables it — linking would
   * 409 — without revealing who holds it (privacy-aware: boolean only).
   */
  isLinkedElsewhere(m: OemMachine): boolean {
    return !this.isLinked(m) && m.linked_elsewhere === true;
  }

  harvesterLabel(id: number | null | undefined): string {
    if (id == null) return '';
    const h = this.harvesters.find((x) => x.id === id);
    return h ? h.model_name || `Harvester ${id}` : `Harvester ${id}`;
  }
}
